/**
 * Conteúdo técnico do Diagweb — a pesquisa, a ficha e o cadastro.
 *
 * **O que é "conteúdo" aqui.** Uma linha da `TabelaPesquisa`: um veículo, um
 * motor, um assunto, um sistema e uma faixa de anos, apontando para o material
 * (um diagrama, um pinout gráfico, um vídeo, um link). É o que o cliente acha
 * quando pesquisa no site.
 *
 * **Volume medido.** 44.497 registros na listagem sem filtro — mas esse número
 * mistura idiomas: são 78.345 linhas em português e 65.655 em espanhol na
 * `TabelaPesquisa`. Por isso o idioma aparece na tela o tempo todo, e a
 * exportação sempre aplica um: sem ele cada conteúdo sai repetido.
 *
 * **Isto é dado global.** Não pertence a projeto nenhum, então a trava do
 * canário recusa toda gravação em desenvolvimento — a API aponta para produção
 * e não existe canário para dado global. As telas avisam antes.
 *
 * **Paginação com teto de 100.** É por isso que a exportação é uma rota própria
 * na API, em streaming: exportar os 44 mil pela listagem seriam 445 requisições.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

/** Idioma do conteúdo. A `TabelaPesquisa` tem uma linha por idioma. */
export const IDIOMAS = [
  { id: 1, nome: "Português", sigla: "Pt" },
  { id: 2, nome: "Espanhol", sigla: "Es" },
  { id: 3, nome: "Inglês", sigla: "En" },
] as const

export function siglaDoIdioma(baseId: number): string {
  return IDIOMAS.find((i) => i.id === baseId)?.sigla ?? String(baseId)
}

export type FiltroDeConteudo = {
  marcaId?: number
  veiculoId?: number
  assuntoId?: number
  sistemaId?: number
  fabSistemaId?: number
  nomeSistemaId?: number
  motorId?: number
  produtoId?: number
  origemId?: number
  baseId?: number
  ordem?: string
}

/** Uma linha da grade. */
export type ConteudoNaLista = {
  dadoId: number
  dadoDesc: string
  marca: string
  veiculo: string
  assunto: string
  sistema: string
  fabSistema: string
  nomeSistema: string
  motor: string
  produto: string
  origem: string
  baseId: number
  anoIni: number
  anoFim: number
  /** Quando foi incluído ou alterado por último — a lista vem nesta ordem. */
  data?: string | null
}

export type PaginaDeConteudo = {
  totalItens: number
  paginaAtual: number
  qtdPorPagina: number
  totalPaginas: number
  itens: ConteudoNaLista[]
}

/** Um item do conteúdo: o arquivo, o link, o diagrama ou o pinout. */
export type ItemDoConteudo = {
  dadoOrdem: number
  dadoTipo: number
  arquivoNome: string
  projetoId: number
  modeloId: number
  diagramaId: number
  componenteId: number
}

export type ConteudoDetalhado = {
  dadoId: number
  baseId: number
  dadoDesc: string
  produtoId: number
  origemId: number
  marcaId: number
  veiculoId: number
  versaoId: number
  sistemaId: number
  assuntoId: number
  fabSistemaId: number
  nomeSistemaId: number
  motorId: number
  modeloId: number
  anoIni: number
  anoFim: number
  data: string
  versao: number
  itens: ItemDoConteudo[]
}

/**
 * O que cada `dadoTipo` significa (RN-10 do módulo). Os números vêm do legado e
 * não são sequenciais por acaso: 1, 3 e 6 são arquivos de formatos diferentes.
 */
export const TIPOS_DE_ITEM: Record<number, string> = {
  1: "Flash (swf)",
  3: "Imagem ou documento",
  5: "Arquivo",
  6: "Imagem PNG",
  7: "Diagrama SVG",
  8: "Pinout gráfico",
  9: "YouTube",
  10: "Vimeo",
  11: "Link",
}

/**
 * As fontes que o front consegue cadastrar inteiras.
 *
 * O legado tem uma sexta — "arquivo", com upload de PDF ou imagem. Ela **existe
 * na API** (`POST api/conteudos/{id}/arquivos`) e mora na ficha do conteúdo, não
 * aqui: o cadastro grava a classificação, o anexo entra depois, com o registro já
 * criado e um id para nomear o arquivo. Ver `arquivosDoConteudo.ts`.
 */
export const FONTES = [
  { chave: "diagramaSVG", rotulo: "Diagrama SVG", precisa: ["projetoId", "diagramaId"] },
  { chave: "pinout_grafico", rotulo: "Pinout gráfico", precisa: ["projetoId", "componenteId", "modeloId"] },
  { chave: "youtube", rotulo: "YouTube", precisa: ["link"] },
  { chave: "vimeo", rotulo: "Vimeo", precisa: ["link"] },
  { chave: "link", rotulo: "Link", precisa: ["link"] },
] as const

export type Fonte = (typeof FONTES)[number]["chave"]

export type NovoConteudo = {
  tipo: Fonte
  produtoId: number
  origemId: number
  baseId: number
  veiculoId?: number
  sistemaId?: number
  assuntoId?: number
  fabSistemaId?: number
  nomeSistemaId?: number
  motorId?: number
  anoInicial?: number
  anoFinal?: number
  descricao: string
  modeloId?: number
  projetoId?: number
  diagramaId?: number
  componenteId?: number
  link?: string
  replicarEspanhol: boolean
}

export type ResultadoDaInclusao = { dadoId: number; mensagem?: string }

/**
 * `foraDoProjeto()` com parâmetros de query.
 *
 * Espalhar o config e acrescentar `params` inline faz o axios inferir o tipo do
 * objeto literal e brigar com o `AxiosRequestConfig` genérico. Tipar como
 * `Record<string, unknown>` uma vez, aqui, evita repetir o contorno em cada
 * chamada — e o `foraDoProjeto` continua sendo a conversa com a trava.
 */
export function globalCom(params: Record<string, unknown>) {
  return { ...foraDoProjeto(), params } as const
}

// ------------------------------------------------------------------ leitura

export function usarConteudos(filtro: FiltroDeConteudo, pagina: number, qtdPorPagina = 50) {
  return useQuery({
    queryKey: ["conteudos", filtro, pagina, qtdPorPagina] as const,
    queryFn: () => obter<PaginaDeConteudo>("api/conteudos", {
      ...filtro, pagina, qtdPorPagina,
    }),
  })
}

export function usarConteudo(dadoId: number, baseId: number) {
  return useQuery({
    queryKey: ["conteudo", dadoId, baseId] as const,
    enabled: dadoId > 0,
    queryFn: () => obter<ConteudoDetalhado>(`api/conteudos/${dadoId}`, { baseId }),
  })
}

/**
 * A consolidação reconstrói a `TabelaPesquisa` a partir das tabelas `dado_*`.
 *
 * O sistema antigo fica perguntando de 5 em 5 minutos se terminou. Aqui não:
 * **zero polling** é regra dura do projeto, então a tela confere ao abrir e
 * quando a pessoa pedir. Em troca, ela não avisa sozinha quando acabar.
 */
export function usarConsolidacaoPendente() {
  return useQuery({
    queryKey: ["conteudo-consolidacao"] as const,
    queryFn: () => obter<{ pendente: boolean; mensagem: string }>(
      "api/conteudos/consolidacao/pendente", {}),
  })
}

// ------------------------------------------------------------------ escrita

function invalidar(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["conteudos"] })
  qc.invalidateQueries({ queryKey: ["conteudo"] })
}

/**
 * Cadastro em lote: uma requisição para as N combinações que a pessoa montou.
 *
 * O sistema antigo faz o produto cartesiano das multi-seleções e grava uma linha
 * por combinação. A API aceita um id por campo, mas tem `POST lote` (até 500),
 * então o cartesiano é montado aqui e mandado de uma vez.
 */
export function usarIncluirConteudos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (itens: NovoConteudo[]) => {
      if (itens.length === 1) {
        const r = await api.post<ResultadoDaInclusao>("api/conteudos", itens[0], foraDoProjeto())
        return [r.data]
      }
      const r = await api.post<ResultadoDaInclusao[]>("api/conteudos/lote", { itens }, foraDoProjeto())
      return r.data
    },
    onSuccess: () => invalidar(qc),
  })
}

export type AlteracaoDeConteudo = {
  descricao?: string
  produtoId: number
  veiculoId?: number
  sistemaId?: number
  assuntoId?: number
  fabSistemaId?: number
  nomeSistemaId?: number
  motorId?: number
  anoInicial?: number
  anoFinal?: number
}

export function usarAlterarConteudo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { dadoId, baseId, dados }: { dadoId: number; baseId: number; dados: AlteracaoDeConteudo },
    ) => {
      await api.put(`api/conteudos/${dadoId}`, dados, globalCom({ baseId }))
    },
    onSuccess: () => invalidar(qc),
  })
}

/**
 * Duplicação: o que vier nulo é copiado da origem.
 *
 * É a peça que faz as combinações extras do "alterar" funcionarem como no
 * legado — ela herda modelo, versão e origem do registro original e **copia os
 * itens**, coisa que o cadastro em lote não faria, porque no lote não há de quem
 * herdar.
 */
export function usarDuplicarConteudo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { dadoId, baseId, dados }:
      { dadoId: number; baseId: number; dados: Partial<AlteracaoDeConteudo> & { copiarItens?: boolean } },
    ) => {
      const r = await api.post<ResultadoDaInclusao>(
        `api/conteudos/${dadoId}/duplicacao`,
        { copiarItens: true, ...dados },
        globalCom({ baseId }))
      return r.data
    },
    onSuccess: () => invalidar(qc),
  })
}

export function usarExcluirConteudo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ dadoId, baseId }: { dadoId: number; baseId: number }) => {
      await api.delete(`api/conteudos/${dadoId}`, globalCom({ baseId }))
    },
    onSuccess: () => invalidar(qc),
  })
}

export function usarSolicitarConsolidacao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ pendente: boolean; mensagem: string }>(
        "api/conteudos/consolidacao", {}, foraDoProjeto())
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conteudo-consolidacao"] }),
  })
}

// ------------------------------------------------------------------ exportação

/**
 * Baixa o CSV do recorte inteiro.
 *
 * Passa pelo cliente axios, e não por um link direto, por dois motivos: o token
 * vive em memória e não em cookie, então um `<a href>` sairia sem autenticação e
 * quebraria no dia em que a rota deixasse de ser aberta; e assim a trava e o
 * tratamento de erro valem aqui como em qualquer outra chamada.
 *
 * O peso não é problema: a exportação inteira em português deu 2,5 MB.
 */
export function usarExportarConteudo() {
  return useMutation({
    mutationFn: async ({ filtro, baseId }: { filtro: FiltroDeConteudo; baseId: number }) => {
      const params: Record<string, number> = {}
      for (const [k, v] of Object.entries(filtro)) {
        if (k === "baseId" || k === "ordem") continue
        if (typeof v === "number" && v > 0) params[k] = v
      }
      // O idioma é sempre aplicado: sem ele cada conteúdo sai uma vez por idioma.
      params.baseId = baseId === 2 ? 2 : 1

      const r = await api.get("api/conteudos/exportar", {
        ...globalCom(params),
        responseType: "blob",
        // A resposta é texto, não JSON — o interceptor que recusa HTML não se aplica.
        transformResponse: (d) => d,
      })

      const nome = nomeNoCabecalho(r.headers?.["content-disposition"])
      baixar(r.data as Blob, nome)
      return nome
    },
  })
}

/** Tira o nome do arquivo do `Content-Disposition`, que a API já monta. */
function nomeNoCabecalho(cabecalho: unknown): string {
  const texto = typeof cabecalho === "string" ? cabecalho : ""
  const achado = /filename="?([^\";]+)"?/i.exec(texto)
  return achado?.[1] ?? "conteudos.csv"
}

function baixar(conteudo: Blob, nome: string) {
  const url = URL.createObjectURL(conteudo)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Sem isso o Blob fica preso na memória da aba até o recarregamento.
  URL.revokeObjectURL(url)
}
